import { ChangeDetectorRef, Directive, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { AbstractItemComponent } from '@df/catalog-ui/abstract-item.component';
import { FilterAttributeOption } from '@df/catalog-ui/listing/filter/filter-attribute-option';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { BundleSelection } from '@df/catalog/product/bundle-selection';
import { IProductConfigurableSelection, IProductDataV2, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { resizeObservable } from '@df/dom/fn/resize-observable';
import { TimeoutService } from '@df/ng/timeout.service';
import { Nexus } from '@tomandco/nexus-api-types';
import each from 'lodash-es/each';
import isEmpty from 'lodash-es/isEmpty';
import { Subscription } from 'rxjs';

export class ProductSelectionEvent<T> {
    constructor(
        public model: T,
        public selection: IProductConfigurableSelection
    ) {}
}

declare module '@df/core/service/df-event' {
    interface IEventType {
        // basket
        productClick: DfEventConfig<
            'productClick',
            {
                product: Product<IProductDataV2>;
                position?: number;
                listId?: string;
                listName?: string;
            },
            void
        >;
    }
}

@Directive()
export abstract class ProductComponentDef<T extends Product<IProductDataV2> = Product<IProductDataV2>>
    extends AbstractItemComponent<T>
    implements OnChanges, OnInit
{
    protected override canDetach = true;
    /**
     * Type id
     */
    type = Product.type;
    /**
     * Configurable selection
     */
    selection?: IProductConfigurableSelection;

    /**
     * Size watch subscription
     */
    private sizeWatchSubscription?: Subscription;

    /**
     * Listing model can be passed to component via binding
     */
    @Input()
    listing?: ProductListing<T>;

    @Input()
    override model!: T;

    /**
     * The ID of the list in which the item was presented to the user. Example value `related-products`
     */
    @Input()
    listId?: string;

    /**
     * The name of the list in which the item was presented to the user. Example value `Related Products`
     */
    @Input()
    listName?: string;

    /**
     * The index/position of the item in a list.
     */
    @Input()
    listPosition?: number;

    /**
     * If grid with colspan/rowspan then this should be passed
     */
    @Input()
    gridColumn?: number;

    @Input()
    gridRowSpan?: number;

    @Output() modelChange = new EventEmitter<T>();

    /** Event emmited on selection data change. */
    @Output() selectionChange = new EventEmitter<ProductSelectionEvent<T>>();

    /**
     * Parent product model
     */
    parent?: T;

    /**
     * Bundle selection model for bundle component
     */
    bundleSelection?: BundleSelection;

    /**
     * Variant id for variant product
     */
    variantId?: number | string;

    /**
     * Buy request data
     */
    request: Omit<Nexus.Lib.Basket.BasketRequestData, 'id' | 'qty'> = {
        options: {},
        bundle_options: {}
    };

    /**
     * Original model comming from listing
     */
    protected originalModel?: T;

    /**
     * If children of the configurable product have been loaded
     */
    protected configurableChildrenLoaded = false;

    /**
     * If we allowe loading configurable children
     * we use false here for legacy support
     */
    protected allowConfigurableChildrenLoad = false;

    constructor(
        changeDetectorRef: ChangeDetectorRef,
        app: App,
        timeoutService: TimeoutService,
        protected elementRef: ElementRef
    ) {
        super(changeDetectorRef, app, timeoutService);
    }

    override ngOnChanges(changes) {
        if (changes.model) {
            if (this.originalModel !== changes.model.currentValue) {
                this.originalModel = changes.model.currentValue;
                this.onOriginalModelChange();
            }
        }
        this.watchSize();
        super.ngOnChanges(changes);
    }

    protected onOriginalModelChange() {
        this.configurableChildrenLoaded = false;
        this.loadConfigurableChildren();
    }

    /**
     * On model change hook to sync data
     */
    protected override onModelChange() {
        if (!this.model) {
            return;
        }
        // bind top level product model to vm
        this.setParent(<T>this.model.parent);
        // initiate subelection model by existing selection or request data
        if (this.model.isBundle) {
            this.bundleSelection = this.model.createBundleSelectionObject(this.bundleSelection || this.request?.bundle_options);
            if (this.request) {
                this.request.bundle_options = this.bundleSelection.selection;
            }
        }

        this.modelChange.emit(this.model);

        super.onModelChange();
    }
    /**
     * Matches child exactly for that selection object
     *
     * (Selection object is object containing only configurable product selection
     * so  _.keys(selection) = product.configurableAttributeCodes_
     *
     * TIP: Use this callback when you want configurable => simple change when all options are selected
     *
     * @param  {any} selection
     */
    matchExactly(selection: IProductConfigurableSelection): boolean {
        const child = this.getExactMatch(selection);
        this.updateModel(child || this.parent);
        this.onSelectionChange(selection);
        return !!child;
    }

    updateModel(model: T | undefined) {
        this.setModel(model);
    }

    protected setParent(model: T | undefined) {
        this.parent = model;
    }

    getExactMatch(selection: IProductConfigurableSelection): T | undefined {
        return this.parent?.matchChild(selection) as T | undefined;
    }

    /**
     * Fills missing selection with configurable defaults then matches child
     *
     * (Selection object is object containing only configurable product selection
     * so  _.keys(selection) = product.configurableAttributeCodes_
     *
     * IMPORTANT: with this function we try to find best child that is IN stock
     *
     * TIP: Use this callback when you want configurable => simple change when any option is selected
     *
     * @param  {any} selection
     */
    matchBest(selection: IProductConfigurableSelection): boolean {
        const defaultChild = this.getBestMatch(selection);
        this.updateModel(<T>defaultChild || this.parent);
        this.onSelectionChange(selection);
        return !!defaultChild;
    }

    getBestMatch(selection: IProductConfigurableSelection): T | undefined {
        if (!this.parent) {
            return undefined;
        }
        const filter = Object.assign({}, this.parent.createSelectionObject(), selection);
        let defaultChild = this.parent.matchChild(filter);
        if (!defaultChild || defaultChild.isOutOfStock) {
            const allChildren = this.parent.matchChildren(Object.assign({}, selection), true) || [];
            const child = allChildren.find(i => !i.isOutOfStock) || allChildren[0];
            if (child) {
                defaultChild = child;
            }
        }
        return defaultChild as T | undefined;
    }

    /**
     * Matches selection for given attrs, does not search for replacement, does not check stock
     * @param {any} selection
     */
    match(selection: IProductConfigurableSelection): boolean {
        if (!this.parent) {
            return false;
        }
        const filter = Object.assign({}, this.parent.createSelectionObject(), selection);
        const child = this.parent.matchChild(filter) || this.parent.matchChild(Object.assign({}, selection), true);
        this.setModel(<T>child || this.parent);
        return !!child;
    }

    /**
     * Triggered on selection changes
     * 1. updates selection object (cretes new)
     * 2. if simples couldnt me matched we check if best match parent can be used, this is useful if children have different parents
     */
    protected onSelectionChange(selection: IProductConfigurableSelection) {
        this.selection = { ...selection };

        this.selectionChange.emit(new ProductSelectionEvent<T>(this.model, this.selection));

        if (this.model.isConfigurable) {
            const bestMatch = this.getBestMatch(selection);
            const bestParent = bestMatch?.parent;
            if (bestParent) {
                this.setModel(bestParent as T);
            }
        }
    }

    /**
     * Takes listing filters selection as object
     * only configurable attris are takenn
     */
    getListingSelection(override?: { [key: string]: number }): { [key: string]: string } | undefined {
        const listing = this.listing;
        const selection: any = {};
        if (listing) {
            const attrs =
                this.model.configurableAttributeCodes ||
                this.app.resources.attribute
                    .getAllExisting()
                    .filter(i => i.data?.configurable)
                    .map(i => i.id);
            if (attrs && attrs.length && this.listing) {
                this.listing
                    // take filters matching attribute codes
                    .getFiltersByAttributes(attrs)
                    // only take filters with selection
                    .filter(filter => filter instanceof FilterAttributeOption)
                    //
                    .forEach(_filter => {
                        const filter = _filter as FilterAttributeOption<T>;
                        let filterSelection: number | undefined;
                        if (override && override[filter.id]) {
                            filterSelection = override[filter.id];
                        } else {
                            filterSelection = filter.selection ? filter.selection[0] : undefined;
                        }
                        if (filterSelection) {
                            selection[filter.id] = filter.attribute.getOptionLabel(filterSelection, filter.options);
                        }
                    });
            }
        }
        if (override) {
            each(override, (value: number | number[], key: string) => {
                if (value && !selection[key]) {
                    const attr = this.app.resources.attribute.get(key);
                    if (attr) {
                        const label = attr.getOptionLabel(value);
                        if (label) {
                            selection[key] = label;
                        }
                    }
                }
            });
        }

        return isEmpty(selection) ? undefined : selection;
    }

    get price() {
        return this.model.getPrice({ ...this.request, qty: 1 });
    }

    get orgPrice() {
        return this.model.getOrgPrice({ ...this.request, qty: 1 });
    }

    /**
     * Provides product component layout in terms of columns proportions
     * Note: allowed layouts are defined in product-listing.component, in grid constructor
     */
    getLayout(): string {
        return [this.gridColumn || 1, this.gridRowSpan || 1].join('x');
    }

    /**
     * Sets up component size watcher
     */
    private watchSize() {
        if (this.requiresSizeWatch()) {
            if (this.sizeWatchSubscription) {
                this.sizeWatchSubscription.unsubscribe();
            }
            const el = this.elementRef.nativeElement;
            this.sizeWatchSubscription = resizeObservable(
                el,
                (event: ResizeObserverEntry) => {
                    const entry = event.borderBoxSize[0];
                    this.onSizeChange({ height: entry.blockSize, width: entry.inlineSize });
                },
                0
            ).subscribe();
        } else {
            this.sizeWatchSubscription?.unsubscribe();
        }
    }

    /**
     * Returns information needed for `gtmTrackSelectItem` directive.
     *
     * @info Ready for GA4
     */
    get gtmTrackSelectItem() {
        return {
            listId: this.listId,
            listName: this.listName,
            index: this.listPosition,
            model: this.model
        };
    }

    /**
     * Retrieves information needed for gtm track click directive
     *
     * @deprecated Use `gtmTrackSelectItem` instead.
     */
    get gtmProductClick() {
        return {
            productId: this.model.id,
            position: this.listPosition,
            list: this.listName
        };
    }

    protected dispatchProductClickEvent() {
        this.app.event.dispatch({
            id: 'productClick',
            data: {
                product: this.model,
                position: this.listPosition,
                listId: this.listId,
                listName: this.listName
            }
        });
    }

    /**
     * If size watch is required
     */
    protected requiresSizeWatch() {
        return false;
    }

    /**
     * On component size change callback
     */
    protected onSizeChange(dims: { height: number; width: number }) {}

    protected override onBreakpoint() {
        this.watchSize();
        super.onBreakpoint();
    }

    /**
     * If confirubale children cna be loaded and used
     */
    protected canUseConfigurableChildren(): boolean {
        return this.allowConfigurableChildrenLoad && this.originalModel?.parent.isConfigurable ? true : false;
    }

    /**
     * Loads children of the configuble product
     */
    private loadConfigurableChildren(): void {
        if (this.allowConfigurableChildrenLoad) {
            this.originalModel?.parent.load(2).then(() => {
                if (!this.canUseConfigurableChildren()) {
                    return;
                }

                const childrenIds = this.originalModel?.parent.childrenIds;

                if (!childrenIds?.length) {
                    return;
                }

                this.app.resources.product.loadAllIds(childrenIds, 2, { safe: true }).finally(() => {
                    this.configurableChildrenLoaded = true;
                    this.onChildrenLoaded();
                });
            });
        }
    }

    /**
     * After children are loaded callback
     */
    protected onChildrenLoaded() {
        this.detectChanges();
    }
}
